import * as THREE from 'three';

/**
 * A lightweight vapour contrail — a single additive line that records the recent world
 * positions of a point (e.g. a plane's tail). Colours fade head→tail so the streak
 * dissolves behind the aircraft; additive blending + the scene bloom give it a soft glow.
 * One geometry per plane (no per-frame entity spam), updated in place.
 */
export class Trail {
  readonly object: THREE.Line;
  private pos: Float32Array;
  private readonly n: number;
  private seeded = false;

  constructor(n = 46, tint = 0xeaf2ff, opacity = 0.5) {
    this.n = n;
    this.pos = new Float32Array(n * 3);
    const colors = new Float32Array(n * 3);
    const c = new THREE.Color(tint);
    for (let i = 0; i < n; i++) {
      const f = 1 - i / (n - 1);      // 1 at the head (plane), 0 at the tail
      const v = f * f;                // ease so it lingers near the head then fades out
      colors[i * 3] = c.r * v; colors[i * 3 + 1] = c.g * v; colors[i * 3 + 2] = c.b * v;
    }
    const geo = new THREE.BufferGeometry();
    geo.setAttribute('position', new THREE.BufferAttribute(this.pos, 3).setUsage(THREE.DynamicDrawUsage));
    geo.setAttribute('color', new THREE.BufferAttribute(colors, 3));
    const mat = new THREE.LineBasicMaterial({
      vertexColors: true, transparent: true, opacity,
      blending: THREE.AdditiveBlending, depthWrite: false,
    });
    this.object = new THREE.Line(geo, mat);
    this.object.frustumCulled = false;
  }

  /** Push the current emit point; shifts the buffer so the line trails the aircraft. */
  update(p: THREE.Vector3) {
    const a = this.pos;
    if (!this.seeded) { for (let i = 0; i < this.n; i++) { a[i * 3] = p.x; a[i * 3 + 1] = p.y; a[i * 3 + 2] = p.z; } this.seeded = true; }
    for (let i = this.n - 1; i > 0; i--) {
      a[i * 3] = a[(i - 1) * 3]; a[i * 3 + 1] = a[(i - 1) * 3 + 1]; a[i * 3 + 2] = a[(i - 1) * 3 + 2];
    }
    a[0] = p.x; a[1] = p.y; a[2] = p.z;
    (this.object.geometry.attributes.position as THREE.BufferAttribute).needsUpdate = true;
  }

  dispose() {
    this.object.geometry.dispose();
    (this.object.material as THREE.Material).dispose();
    this.object.removeFromParent();
  }
}
